<template>
  <div class="notification">
    <div id="notificacion-btn-bell" class="notification-btn animate__animated"
      :class="{ hide: notificationSectionOpened || !someCurrentNotification }"
      title="Abrir panel de notificaciones"
      @click="showNotifications">
      <i class="far fa-bell bell-icon"></i>
      <span v-show="newNotifications" class="notification__count"></span>
    </div>
    <section class="notification-section" :class="{ open: notificationSectionOpened }">
      <div class="notification-section__panel-header">
        <h5>Notificaciones</h5>
        <span class="notification-section__close-btn" @click="showNotifications"
          title="Cerrar panel de notificaciones"><i class="fal fa-times"></i></span>
      </div>
      <!-- Filters -->
      <div class="notification-section__filters" v-if="atLeastTwoNotificationList">
        <ul class="filters">
          <li title="Tareas en proceso" v-show="processingAndOther" class="filters__item" :class="{ 'active': filterByProcessing }" @click="filterNotifications('PROCESSING')" ><i class="fad fa-spinner-third fa-spin"></i></li>
          <li title="Tareas finalizadas" v-show="doneAndOther" class="filters__item" :class="{ 'active': filterByDone }" @click="filterNotifications('DONE')" ><i class="fas fa-check"></i></li>
          <li title="Tareas con error" v-show="errorAndOther" class="filters__item" :class="{ 'active': filterByError }" @click="filterNotifications('ERROR')" ><i class="fas fa-exclamation"></i></li>
          <li title="Tareas canceladas" v-show="canceledAndOther" class="filters__item" :class="{ 'active': filterByCanceled }" @click="filterNotifications('CANCELED')" ><i class="fas fa-ban"></i></li>
          <li title="Todas las tareas" class="filters__item" :class="{ 'active': showAllNotifications }" @click="filterNotifications('ALL')" >{{ currentTasksTotal }}</li>
        </ul>
      </div>
      <div class="notification-section__panel-body" :style="atLeastTwoNotificationList ? 'height: calc(100% - 6rem);' : 'height: calc(100% - 3rem);'">
        <!-- NEW, WAITING, PROCESSING TASKS NOTIFICATIONS -->
        <div v-show="showAllNotifications || filterByProcessing">
          <AsyncTaskNotificationSection
            status="PROCESSING"
            :title="processingTaskListTitle"
            :show="processingTasksCount > 0"
          >
            <template v-slot:section-icon><i class="fad fa-spinner-third fa-spin"></i></template>
          </AsyncTaskNotificationSection>
          <AsyncTaskNotificationCard v-for="task in currentProcessingTasks" :key="task.id"
            :task="task"
            :canCancelTask="permissionToCancelTask(task)"
            :remainingTime="task.remainingTime"
            @cancel-task="cancelTaskById"
          ></AsyncTaskNotificationCard>
        </div>
        <!-- DONE TASKS NOTIFICATIONS -->
        <div v-show="showAllNotifications || filterByDone">
          <AsyncTaskNotificationSection :clearSection="true" status="DONE" :title="doneTaskListTitle" :show="doneTasksCount > 0" @clear-notifications="deleteAllNotificationsByStatus">
            <template v-slot:section-icon><i class="fas fa-check"></i></template>
          </AsyncTaskNotificationSection>
          <AsyncTaskNotificationCard v-for="task in currentDoneTasks" :key="task.id"
            :task="task"
            :canRemoveTask="REMOVE_NOTIFICATION"
            @hide-task="sendTaskToBlacklist"
          ></AsyncTaskNotificationCard>
        </div>

        <!-- ERROR TASKS NOTIFICATIONS -->
        <div v-show="showAllNotifications || filterByError">
          <AsyncTaskNotificationSection :clearSection="true" status="ERROR" :title="errorTaskListTitle" :show="errorTasksCount > 0" @clear-notifications="deleteAllNotificationsByStatus">
            <template v-slot:section-icon><i class="fas fa-exclamation"></i></template>
          </AsyncTaskNotificationSection>
          <AsyncTaskNotificationCard v-for="task in currentErrorTasks" :key="task.id"
            :task="task"
            :canRemoveTask="REMOVE_NOTIFICATION"
            @hide-task="sendTaskToBlacklist"/>
        </div>

        <!-- CANCELED TASKS NOTIFICATIONS -->
        <div v-show="showAllNotifications || filterByCanceled">
          <AsyncTaskNotificationSection :clearSection="true" status="CANCELED" :title="canceledTaskListTitle" :show="canceledTasksCount > 0" @clear-notifications="deleteAllNotificationsByStatus">
            <template v-slot:section-icon><i class="fas fa-ban"></i></template>
          </AsyncTaskNotificationSection>
          <AsyncTaskNotificationCard v-for="task in currentCanceledTasks" :key="task.id"
            :task="task"
            :canRemoveTask="REMOVE_NOTIFICATION"
            @hide-task="sendTaskToBlacklist"/>
        </div>

        <div class="btn btn-secondary btn-sm rounded-pill w-50 mx-auto my-4" title="Borrar todas las notificaciones"
          v-if="doneTasksCount > 0 || errorTasksCount > 0 || canceledTasksCount > 0" @click="deleteAllNotifications">Borrar todo</div>
        <div v-if="!someCurrentNotification" class="text-muted my-4 mx-auto">
          <p>No hay notificaciones por mostrar</p>
          <span class="d-block w-100 text-center display-4"><i class="fal fa-smile"></i></span>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { AsyncTask, AsyncTaskFilters, AsyncTaskStatus, AsyncTaskType } from '@/types/catalogs/AsyncTask'
import AsyncTaskNotificationSection from './AsynTaskNotificationSection.vue'
import AsyncTaskNotificationCard from './AsyncTaskNotificationCard.vue'
import Vue, { VNode } from 'vue'
import CatalogsFillsService from '@/services/CatalogsFillsService'
import userInfo from '@/store/userInfo'
import { NUMBER } from '@/types/GeneralConstants'
import { PermissionNames } from '@/types/Auth'
import LogService from '@/services/support/LogService'
import { PageRequest, SortDirection } from '@/types/Support'
import { mapActions, mapMutations, mapGetters } from 'vuex'

export default Vue.extend({
  components: {
    AsyncTaskNotificationSection,
    AsyncTaskNotificationCard
  },
  data () {
    return {
      PAGE_SIZE: 100,
      LONG_INTERVAL: 4,
      SHORT_INTERVAL: 2,
      REMOVE_NOTIFICATION: true,
      // HAy que meter una variable para aplicar el filtro de las tareas
      filterByProcessing: false,
      filterByDone: false,
      filterByError: false,
      filterByCanceled: false,
      showAllNotifications: true,

      notificationSectionOpened: false,
      swingNotificationButton: false,

      taskDone: false,
      taskError: false,
      taskCanceled: false,
      // This lists render the notification cards
      currentProcessingTasks: [] as AsyncTask[],
      currentDoneTasks: [] as AsyncTask[],
      currentErrorTasks: [] as AsyncTask[],
      currentCanceledTasks: [] as AsyncTask[],
      // This maps will help to trace the time remaining between the current and the latest queried task
      // Each time some MAP is updated by 'set' is required update its corresponding list from above
      PROCESSING_TASKS_MAP: new Map<number, AsyncTask>(),
      DONE_TASKS_MAP: new Map<number, AsyncTask>(),
      ERROR_TASKS_MAP: new Map<number, AsyncTask>(),
      CANCELED_TASKS_MAP: new Map<number, AsyncTask>(),

      // if this is active, the reallocation process wait LONG_INTERVAL seconds to check notification changes
      sleepProcessingTasksReallocation: true,

      newNotifications: false
    }
  },
  async created () {
    await this.deleteExpiredStoredTasks()
    this.sincronizedAsyncTasks()
      .then(() => {
        this.loadDoneTasksFromList(this.getDoneTasksStored())
      })
  },
  async mounted () {
    this.checkTaskNotifications(this.checkChangesInProcessingTasks)
  },
  computed: {
    permissionToReadTasks (): boolean {
      return userInfo.hasPermission(PermissionNames.TASKS_READ)
    },
    permissionToCancelDatafeedTask (): boolean {
      return userInfo.hasPermission(PermissionNames.DATAFEED_DELETE)
    },
    permissionToCancelIntegrationTask (): boolean {
      return userInfo.hasPermission(PermissionNames.INTEGRATION_DELETE)
    },
    someCurrentNotification (): boolean {
      return this.currentTasksTotal > 0
    },
    processingTasksCount (): number {
      return this.currentProcessingTasks.length
    },
    doneTasksCount (): number {
      return this.currentDoneTasks.length
    },
    errorTasksCount (): number {
      return this.currentErrorTasks.length
    },
    canceledTasksCount (): number {
      return this.currentCanceledTasks.length
    },
    currentTasksTotal (): number {
      let count = 0
      if (this.showAllNotifications || this.filterByProcessing) {
        count += this.processingTasksCount
      }
      if (this.showAllNotifications || this.filterByDone) {
        count += this.doneTasksCount
      }
      if (this.showAllNotifications || this.filterByError) {
        count += this.errorTasksCount
      }
      if (this.showAllNotifications || this.filterByCanceled) {
        count += this.canceledTasksCount
      }
      return count
    },
    processingTaskListTitle (): String {
      return (this.processingTasksCount > 1
        ? 'Tareas en proceso'
        : 'Tarea en proceso') + `(${this.processingTasksCount})`
    },
    doneTaskListTitle (): String {
      return (this.doneTasksCount > 1
        ? 'Tareas finalizadas'
        : 'Tarea finalizada') + `(${this.doneTasksCount})`
    },
    errorTaskListTitle (): String {
      return (this.errorTasksCount > 1
        ? 'Tareas con error'
        : 'Tarea con error') + `(${this.errorTasksCount})`
    },
    canceledTaskListTitle (): String {
      return (this.canceledTasksCount > 1
        ? 'Tareas canceladas'
        : 'Tarea cancelada') + `(${this.canceledTasksCount})`
    },
    /** @returns true if exist some processing task notification and another with a different type */
    processingAndOther (): boolean {
      return this.processingTasksCount > 0 && (this.doneTasksCount > 0 || this.errorTasksCount > 0 || this.canceledTasksCount > 0)
    },
    /** @returns true if exist some done task notification and another with a different type */
    doneAndOther (): boolean {
      return this.doneTasksCount > 0 && (this.errorTasksCount > 0 || this.canceledTasksCount > 0 || this.processingTasksCount > 0)
    },
    /** @returns true if exist some error task notification and another with a different type */
    errorAndOther (): boolean {
      return this.errorTasksCount > 0 && (this.processingTasksCount > 0 || this.doneTasksCount > 0 || this.canceledTasksCount > 0)
    },
    /** @returns true if exist some canceled task notification and another with a different type */
    canceledAndOther (): boolean {
      return this.canceledTasksCount > 0 && (this.processingTasksCount > 0 || this.doneTasksCount > 0 || this.errorTasksCount > 0)
    },
    /** @returns true if exist at least two notifications with different type */
    atLeastTwoNotificationList (): boolean {
      return this.processingAndOther || this.doneAndOther || this.errorAndOther || this.canceledAndOther
    }
  },
  methods: {
    ...mapActions({
      reallocateProcessingTasks: 'taskNotifications/reallocateProcessingTasks',
      cancelTaskStored: 'taskNotifications/cancelTask',
      sendTaskToBlacklistStored: 'taskNotifications/blacklistTaskAsync',
      deleteExpiredStoredTasks: 'taskNotifications/deleteExpiredStoredTasks'
    }),
    ...mapMutations({
      storeTask: 'taskNotifications/addTask',
      removeTask: 'taskNotifications/removeTask'
    }),
    ...mapGetters({
      getProcessingTasksStoraged: 'taskNotifications/processingTasks',
      getDoneTasksStored: 'taskNotifications/doneTasks',
      getErrorTasksStored: 'taskNotifications/errorTasks',
      getCanceledTasksStored: 'taskNotifications/canceledTasks'
    }),
    async sincronizedAsyncTasks () {
      if (this.permissionToReadTasks) {
        const pageForWaiting = this.prepareAsyncTasksPageRequest(AsyncTaskStatus.WAITING)
        await this.loadWaitingTasks(pageForWaiting, NUMBER.ZERO)
        const pageForNew = this.prepareAsyncTasksPageRequest(AsyncTaskStatus.NEW)
        await this.loadNewTasks(pageForNew, NUMBER.ZERO)
        const pageForProcessing = this.prepareAsyncTasksPageRequest(AsyncTaskStatus.PROCESSING)
        await this.loadProcessingTasks(pageForProcessing, NUMBER.ZERO)
        this.updateCurrentProcessingTasks()

        const pageForDone = this.prepareAsyncTasksPageRequest(AsyncTaskStatus.DONE)
        await this.loadDoneTasks(pageForDone, NUMBER.ZERO)
        this.updateCurrentDoneTasks()
      }
    },
    permissionToCancelTask (task: AsyncTask) {
      if (task.type === AsyncTaskType.DATAFEED) {
        return this.permissionToCancelDatafeedTask
      } else if (task.type === AsyncTaskType.INTEGRATION) {
        return this.permissionToCancelIntegrationTask
      }
      return false
    },
    storeTaskByStatus (status: AsyncTaskStatus, task: AsyncTask) {
      this.storeTask(
        {
          taskState: status,
          task: { id: task.id, type: task.type, updateDate: task.updateDate }
        }
      )
    },
    async loadWaitingTasks (pageRequest: PageRequest<AsyncTaskFilters>, fetched: number) {
      const response = await CatalogsFillsService.getAsyncTasks(pageRequest)
      fetched += response.size
      for (const taskParams of response.results) {
        let task = AsyncTask.createInstance(taskParams)
        if (
          this.getProcessingTasksStoraged().every((currentTask: any) => currentTask.id !== task.id) &&
          this.isValidNotificationToSincronized(task)
        ) {
          this.storeTaskByStatus(task.status, task)
          this.PROCESSING_TASKS_MAP.set(task.id, task)
        }
      }
      if (fetched < response.total) {
        pageRequest.page++
        await this.loadWaitingTasks(pageRequest, fetched)
      }
    },
    async loadNewTasks (pageRequest: PageRequest<AsyncTaskFilters>, fetched: number) {
      const response = await CatalogsFillsService.getAsyncTasks(pageRequest)
      fetched += response.size
      for (const taskParams of response.results) {
        let task = AsyncTask.createInstance(taskParams)
        if (
          this.getProcessingTasksStoraged().every((currentTask: any) => currentTask.id !== task.id) &&
          this.isValidNotificationToSincronized(task)
        ) {
          this.storeTaskByStatus(task.status, task)
          this.PROCESSING_TASKS_MAP.set(task.id, task)
        }
      }
      if (fetched < response.total) {
        pageRequest.page++
        await this.loadNewTasks(pageRequest, fetched)
      }
    },
    async loadProcessingTasks (pageRequest: PageRequest<AsyncTaskFilters>, fetched: number) {
      const response = await CatalogsFillsService.getAsyncTasks(pageRequest)
      fetched += response.size
      for (const taskParams of response.results) {
        let task = AsyncTask.createInstance(taskParams)
        if (
          this.getProcessingTasksStoraged().every((currentTask: any) => currentTask.id !== task.id) &&
          this.isValidNotificationToSincronized(task)
        ) {
          this.storeTaskByStatus(task.status, task)
          this.PROCESSING_TASKS_MAP.set(task.id, task)
        }
      }
      if (fetched < response.total) {
        pageRequest.page++
        await this.loadProcessingTasks(pageRequest, fetched)
      }
    },
    async loadDoneTasks (pageRequest: PageRequest<AsyncTaskFilters>, fetched: number) {
      const response = await CatalogsFillsService.getAsyncTasks(pageRequest)
      fetched += response.size
      for (const taskParams of response.results) {
        let task = AsyncTask.createInstance(taskParams)
        if (
          this.getDoneTasksStored().every((currentTask: any) => currentTask.id !== task.id) &&
          this.isValidNotificationToSincronized(task)
        ) {
          this.storeTaskByStatus(task.status, task)
          this.DONE_TASKS_MAP.set(task.id, task)
        }
      }
      if (fetched < response.total) {
        pageRequest.page++
        await this.loadDoneTasks(pageRequest, fetched)
      }
    },
    prepareAsyncTasksPageRequest (status: AsyncTaskStatus): PageRequest<AsyncTaskFilters> {
      let pageRequest = new PageRequest<AsyncTaskFilters>()
      pageRequest.size = this.PAGE_SIZE
      pageRequest.sort = 'id'
      pageRequest.page = NUMBER.ZERO
      pageRequest.filters = {
        domain: userInfo.inRootDomain()
          ? userInfo.userInfo.domain.id
          : undefined,
        status: status
      }
      return pageRequest
    },
    isValidNotificationToSincronized (task: AsyncTask): boolean {
      return task.availableResultsLink() && !task.isTaskInBlacklistNotifications()
    },
    /**
     * To active the reactivity for the 'currentProcessingTasks' list
     */
    updateCurrentProcessingTasks () {
      this.currentProcessingTasks = this.mapToOrderedList(this.PROCESSING_TASKS_MAP)
    },
    /**
     * To active the reactivity for the 'currentDoneTasks' list
     */
    updateCurrentDoneTasks () {
      this.currentDoneTasks = this.mapToOrderedList(this.DONE_TASKS_MAP)
    },
    /**
     * To active the reactivity for the 'currentErrorTasks' list
     */
    updateCurrentErrorTasks () {
      this.currentErrorTasks = this.mapToOrderedList(this.ERROR_TASKS_MAP)
    },
    /**
     * To active the reactivity for the 'currentCanceledTasks' list
     */
    updateCurrentCanceledTasks () {
      this.currentCanceledTasks = this.mapToOrderedList(this.CANCELED_TASKS_MAP)
    },
    async checkChangesInProcessingTasks () {
      await this.reallocateProcessingTasks() // Reallocate all tasks in the store
        .then(({ inProgress, reallocated }: { inProgress: AsyncTask[], reallocated: AsyncTask[]}) => {
          if (inProgress.length === 0 && reallocated.length === 0) return

          // Update the times for each task that still in progress
          for (const taskInprogress of inProgress) {
            const oldTask = this.PROCESSING_TASKS_MAP.get(taskInprogress.id)
            if (oldTask) {
              AsyncTask.setRemainingTimeBetweenTasks(taskInprogress, oldTask)
              if (taskInprogress.type === AsyncTaskType.DATAFEED) {
                AsyncTask.setRemainingAttachmentTimeBetweenTasks(taskInprogress, oldTask)
              }
            }
            this.PROCESSING_TASKS_MAP.set(taskInprogress.id, taskInprogress)
            this.updateCurrentProcessingTasks()
          }

          // Reallocate each task from the processing task to its new state
          for (const reallocatedTask of reallocated) {
            if (reallocatedTask.status === AsyncTaskStatus.DONE) {
              this.DONE_TASKS_MAP.set(reallocatedTask.id, reallocatedTask)
              this.updateCurrentDoneTasks()
              if (reallocatedTask.type === AsyncTaskType.DATAFEED) {
                this.showDatafeedToast('Descarga finalizada', reallocatedTask.id, reallocatedTask.template.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'success')
              } else if (reallocatedTask.type === AsyncTaskType.INTEGRATION) {
                this.showIntegrationToast('Integración finalizada', reallocatedTask.id, reallocatedTask.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'success')
              }
            }
            if (reallocatedTask.status === AsyncTaskStatus.ERROR) {
              this.ERROR_TASKS_MAP.set(reallocatedTask.id, reallocatedTask)
              this.updateCurrentErrorTasks()
              if (reallocatedTask.type === AsyncTaskType.DATAFEED) {
                this.showDatafeedToast('Error en descarga', reallocatedTask.id, reallocatedTask.template.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'danger')
              } else if (reallocatedTask.type === AsyncTaskType.INTEGRATION) {
                this.showIntegrationToast('Error en integración', reallocatedTask.id, reallocatedTask.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'danger')
              }
            }
            if (reallocatedTask.status === AsyncTaskStatus.CANCELED) {
              this.CANCELED_TASKS_MAP.set(reallocatedTask.id, reallocatedTask)
              this.updateCurrentCanceledTasks()
              if (reallocatedTask.type === AsyncTaskType.DATAFEED) {
                this.showDatafeedToast('Descarga cancelada', reallocatedTask.id, reallocatedTask.template.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'secondary')
              } else if (reallocatedTask.type === AsyncTaskType.INTEGRATION) {
                this.showIntegrationToast('Integración cancelada', reallocatedTask.id, reallocatedTask.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'secondary')
              }
            }
            this.PROCESSING_TASKS_MAP.delete(reallocatedTask.id)
            this.updateCurrentProcessingTasks()
          }
          if (reallocated.length > 0 && !this.notificationSectionOpened) {
            this.swingBellBtn()
            this.newNotifications = true
          }
          this.sleepProcessingTasksReallocation = this.currentProcessingTasks.length === 0
        })
    },
    showDatafeedToast (title: string, id: number, template: string, date: string, requestId: string, variant: string) {
      const dateOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      const dateFormat = Intl.DateTimeFormat('es-MX', dateOpts).format(new Date(`${date}Z`))
      const h = this.$createElement
      const node = h(
        'div', { class: ['d-flex', 'flex-column', 'p-0'] }, [
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `ID:`), `${id}`
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Template:`), template
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Fecha:`), dateFormat
          ]),
          h('p', { class: 'text small m-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Request:`), requestId
          ])
        ])
      this.showToast(`${title}. [Mis Tareas]`, node, variant)
    },
    showIntegrationToast (title: string, id: number, filename: string, date: string, requestId: string, variant: string) {
      const dateOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      const dateFormat = Intl.DateTimeFormat('es-MX', dateOpts).format(new Date(`${date}Z`))
      const h = this.$createElement
      const node = h(
        'div', { class: ['d-flex', 'flex-column', 'p-0'] }, [
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `ID:`), `${id}`
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Archivo:`), filename
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Fecha:`), dateFormat
          ]),
          h('p', { class: 'text small m-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Request:`), requestId
          ])
        ])
      this.showToast(`${title}. [Integración]`, node, variant)
    },
    showToast (title: string, node: VNode, variant: string) {
      this.$bvToast.toast([node], {
        title,
        variant,
        solid: true,
        autoHideDelay: 7500
      })
    },
    async loadDoneTasksFromList (tasks: {id: number, type: AsyncTaskStatus, updateDate: string}[]) {
      const promises = []
      for (const task of tasks) {
        const request = CatalogsFillsService.getAsyncTaskById(task.id)
        promises.push(request)
      }
      return Promise.allSettled(promises)
        .then((taskPromises) => {
          for (const taskPromise of taskPromises) {
            if (taskPromise.status === 'fulfilled') {
              const task = AsyncTask.createInstance(taskPromise.value)
              if (task.status === AsyncTaskStatus.DONE) {
                this.DONE_TASKS_MAP.set(task.id, task)
              }
            }
          }
          this.updateCurrentDoneTasks()
        })
    },
    mapToOrderedList (map: Map<number, AsyncTask>, order: string = SortDirection.DESC): Array<AsyncTask> {
      let array = Array.from(map.values())
      array = array.sort((a: AsyncTask, b: AsyncTask) => a.id >= b.id ? 1 : -1)
      return order === SortDirection.DESC ? array.reverse() : array
    },
    swingBellBtn (): void {
      if (this.swingNotificationButton) return
      this.swingNotificationButton = true
      const bellBtn = document.getElementById('notificacion-btn-bell')
      bellBtn?.classList.add('animate__swing', 'animate__pulse')
      setTimeout(() => {
        this.swingNotificationButton = false
        bellBtn?.classList.remove('animate__swing', 'animate__pulse')
      }, 1000)
    },
    /**
     * To hide and show the notification panel
     */
    showNotifications (): void {
      this.notificationSectionOpened = !this.notificationSectionOpened
      if (this.notificationSectionOpened) this.newNotifications = false
    },
    /**
     * Infinity cicle to call the checkFunction.
     * This is used to check new tasks in progress.
     * @param checkFunction - fuction to call every time after X seconds
     * */
    async checkTaskNotifications (checkFunction: Function) {
      await checkFunction()
      const seconds = this.sleepProcessingTasksReallocation ? this.LONG_INTERVAL : this.SHORT_INTERVAL
      setTimeout(() => {
        this.checkTaskNotifications(checkFunction)
      }, seconds * 1000)
    },
    /**
     * Delete a task from a map
     * @param taskId task id to delete
     * @param map map from where delete
     */
    deleteTaskFromMap (taskId: number, map: Map<number, AsyncTask>) {
      map.delete(taskId)
    },
    /**
     * Delete all the notifications with a final status storaged in localStorage
     */
    deleteAllNotifications () {
      for (const state of [AsyncTaskStatus.DONE, AsyncTaskStatus.ERROR, AsyncTaskStatus.CANCELED]) {
        this.deleteAllNotificationsByStatus(state)
      }
    },
    /**
     * Delete all the notifications in a specific status
     * @param state - section to remove
     */
    deleteAllNotificationsByStatus (state: AsyncTaskStatus) {
      if (state === AsyncTaskStatus.DONE) {
        const doneTasks = this.getDoneTasksStored()
        for (const task of doneTasks) this.sendTaskToBlacklist(AsyncTaskStatus.DONE, task.id, task.type, task.updateDate)
      }
      if (state === AsyncTaskStatus.ERROR) {
        const errorTasks = this.getErrorTasksStored()
        for (const task of errorTasks) this.sendTaskToBlacklist(AsyncTaskStatus.ERROR, task.id, task.type, task.updateDate)
      }
      if (state === AsyncTaskStatus.CANCELED) {
        const canceledTasks = this.getCanceledTasksStored()
        for (const task of canceledTasks) this.sendTaskToBlacklist(AsyncTaskStatus.CANCELED, task.id, task.type, task.updateDate)
      }
      this.filterNotifications('ALL')
    },
    /**
     * Turn on/off the flag to show the corresponding notifications
     * This works as a filter
     *
     * @param section of the notifications
     */
    filterNotifications (section: string): void {
      if (section === AsyncTaskStatus.PROCESSING) {
        this.showAllNotifications = false
        this.filterByProcessing = !this.filterByProcessing
      }
      if (section === AsyncTaskStatus.DONE) {
        this.showAllNotifications = false
        this.filterByDone = !this.filterByDone
      }
      if (section === AsyncTaskStatus.ERROR) {
        this.showAllNotifications = false
        this.filterByError = !this.filterByError
      }
      if (section === AsyncTaskStatus.CANCELED) {
        this.showAllNotifications = false
        this.filterByCanceled = !this.filterByCanceled
      }
      const filters = [this.filterByProcessing, this.filterByDone, this.filterByError, this.filterByCanceled]
      if (section === 'ALL' || filters.every(filter => !filter)) {
        this.showAllNotifications = true
        this.filterByProcessing = false
        this.filterByDone = false
        this.filterByError = false
        this.filterByCanceled = false
      }
    },
    async cancelTaskById (taskId: number): Promise<void> {
      const taskToCancel = this.PROCESSING_TASKS_MAP.get(taskId)
      if (taskToCancel !== undefined) {
        await CatalogsFillsService.deleteTask(taskId)
          .then((response) => {
            LogService.warn(`Tarea ${response.id} cancelada manualmente.`)
          })
          .catch(() => {
            LogService.error(`Tarea ${taskId} no pudo ser cancelada.`)
          })
      }
    },
    /**
     * Send a task to the 'BLACKLIST' section (this tasks will not be display anymore)
     * Both are update storage and current list
     */
    sendTaskToBlacklist (state: AsyncTaskStatus, id: number, type: AsyncTaskType, updateDate: string) {
      this.sendTaskToBlacklistStored({
        taskState: state,
        task: { id, type, updateDate }
      })
      if (state === AsyncTaskStatus.DONE) {
        this.deleteTaskFromMap(id, this.DONE_TASKS_MAP)
        this.updateCurrentDoneTasks()
      }
      if (state === AsyncTaskStatus.ERROR) {
        this.deleteTaskFromMap(id, this.ERROR_TASKS_MAP)
        this.updateCurrentErrorTasks()
      }
      if (state === AsyncTaskStatus.CANCELED) {
        this.deleteTaskFromMap(id, this.CANCELED_TASKS_MAP)
        this.updateCurrentCanceledTasks()
      }
    }
  }
})
</script>

<style lang="scss" scoped>

.animate__swing {
  --animate-duration: 1s;
  --animate-repeat: 2;
}

// Button stylies
div.notification-btn {
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  top: 3.2rem;
  right: 0;
  border-radius: 10rem;
  background-color: var(--sidebar-bg-color);
  box-shadow: 0px 2px 8px 0px var(--secondary);
  transition-duration: 0.2s;
  z-index: 1000;

  &.hide {
    display: none;
  }

  .bell-icon {
    display: block;
    width: fit-content;
    height: fit-content;
    padding: 0.5rem;
    margin: auto;
    font-size: 1.5rem;
    transition-duration: inherit;
  }

  .notification__count {
    display: block;
    position: absolute;
    top: -0.4em;
    left: -0.2em;
    width: 1rem;
    height: 1rem;
    text-align: center;
    font-size: 0.8rem;
    font-weight: bold;
    border-radius: 9rem;
    background-color: var(--danger);
    color: var(--white);
  }
}

$notification-section-panel: 350px;

// Panel stylies
section.notification-section {
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  right: 0;
  display: block;
  transition-duration: 300ms;
  width: $notification-section-panel;
  height: 100vh;
  box-shadow: 0 0 12px #ccc;
  z-index: 1000;

  &:not(.open) {
    right: $notification-section-panel * -1;
    transition-duration: 300ms;
  }

  .notification-section__filters {
    display:  inline-block;
    width: 100%;
    height: fit-content;
    background-color: var(--sidebar-bg-color);

    .filters {
      display: flex;
      flex-direction: row;
      justify-content: space-around;
      padding: 0 0.5rem 0.5rem;
      margin: 0;

      .filters__item {
        display: inline-block;
        margin: 0;
        padding: 0.5rem;
        width: 2.4rem;
        height: 2.4rem;
        text-align: center;
        list-style: none;
        border-radius: 9rem;
        background-color: var(--element-hover-bg-color);
        box-shadow: 0 2px 4px var(--secondary);
        transition-duration: 300ms;
        cursor: pointer;

        &.active {
          background-color: var(--primary);
          color: var(--white);
        }
      }
    }
  }

  .notification-section__panel-header {
    display: flex;
    justify-content: space-between;
    height: 3rem;
    background-color: var(--sidebar-bg-color);

    h5 {
      height: fit-content;
      padding: 0.75rem 1rem;
      text-align: center;
      margin: 0;
    }

    span.notification-section__close-btn {
      display: block;
      width: 3rem;
      height: 3rem;
      padding: 0.5rem;
      font-size: 1.5rem;
      text-align: center;
      border-radius: 9rem;
      cursor: pointer;
    }
  }

  .notification-section__panel-body {
    display: flex;
    flex-direction: column;
    background-color: var(--sidebar-bg-color);
    overflow-y: auto;
    overflow-x: hidden;
  }

  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  ::-webkit-scrollbar-thumb {
    background: #888;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}

@media screen and (hover: hover) {
  div.notification-btn:hover {
    width: 3rem;
    height: 3rem;
    color: var(--font-color-hover);
    background-color: var(--element-hover-bg-color);
    transition-duration: 0.2s;

    .bell-icon {
      padding: 0.7rem;
      font-size: 1.75rem;
      line-height: 1.5rem;
      transition-duration: inherit;
    }
  }

  span.notification-section__close-btn:hover {
    background-color: var(--white);
  }
  .notification-section > .notification-section__filters > .filters > .filters__item:hover {
    background-color: var(--white);
    box-shadow: 0 2px 1px var(--secondary);
    transition-duration: 300ms;
  }
}
</style>
